import { FC, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FlightDetails, GrossAndTax, Outbound } from 'ts/flight/interfaces/FlightDetailsResponse';
import { getCurrency, formatPriceCurrency } from 'utils/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronCircleDown, faChevronCircleUp } from '@fortawesome/free-solid-svg-icons';
import Tooltip from 'components/ui/Tooltip/Tooltip';
import { getPassengerStr, formatPriceFlight, getElapsedStr } from 'utils/flight/flight';
import ReactCountryFlag from 'react-country-flag';
import ProposalHeart from 'components/shared/Proposal/Create/ProposalHeart';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { dateFormat } from 'utils/shared/utils';
import { MainContext } from 'contexts/MainContext';
import { isTT } from 'constants/generic';

interface OrderDetailsProps {
  flight: FlightDetails;
  bounds: Outbound[];
  mapUrl?: string;
  searchToken: string;
}

const OrderDetails: FC<OrderDetailsProps> = ({ flight, bounds, ...props }) => {
  const [t] = useTranslation();
  const [shownDetailsIndex, setShownDetailsIndex] = useState(-1);
  const { proposal, setProposal } = useContext(MainContext);

  const getDurationText = () => {
    const durations = bounds.map((bound) => getElapsedStr(t, bound.elapsedDTime));
    return durations.join(' / ');
  };

  const getAirlinesText = () => {
    const airlines = bounds.map((bound) => {
      return bound.airline.desc;
    });
    return airlines.join(' / ');
  };

  const getFlightFromText = () => {
    const desc = bounds[0].departureAirport.desc;
    const city = bounds[0].departureAirport.cityDesc;
    const code = bounds[0].departureAirport.code;

    return `${city} - ${code} <span class="text-normal">${desc}</span>`;
  };

  const getFlightToText = () => {
    const desc = bounds[0].arrivalAirport.desc;
    const city = bounds[0].arrivalAirport.cityDesc;
    const code = bounds[0].arrivalAirport.code;

    return `${city} - ${code} <span class="text-normal">${desc}</span>`;
  };

  const recountPrice = () => {
    const price = flight.flightPricesByPDT.reduce((acc, passenger) => {
      const price = passenger.grossAndTaxWithMarkup[
        passenger.grossAndTaxWithMarkup.currency.toLowerCase() as keyof GrossAndTax
      ] as number;
      return acc + price;
    }, 0);

    return formatPriceCurrency(price, getCurrency(flight.priceCurrency));
  };

  const onClickChevron = (index: number) => {
    setShownDetailsIndex(shownDetailsIndex === -1 ? index : -1);
  };

  const includesUsa = () => {
    return bounds.some((bound) =>
      bound.gdsFlyAirs.some(
        (gds) =>
          gds.arrivalAirport.countryCode === 'US' || gds.departureAirport.countryCode === 'US'
      )
    );
  };

  const onClickHeart = () => {
    if (!proposal || !setProposal) return;
    const existingProposalIndex = proposal.flights.findIndex(
      (proposal) => proposal.item.id === flight.id
    );

    if (existingProposalIndex !== -1) {
      const updatedFlights = proposal.flights.filter((_, index) => index !== existingProposalIndex);
      setProposal({ ...proposal, flights: updatedFlights });
    } else {
      const newProposal = {
        item: JSON.parse(JSON.stringify(flight)),
        currency: flight.flighPrice.grossAndTaxWithMarkup.currency,
        price: flight.flighPrice.grossAndTaxWithMarkup.value,
        displayPrice: true,
        searchToken: props.searchToken,
      };

      setProposal({ ...proposal, flights: [...proposal.flights, newProposal] });
    }
  };

  return (
    <div className="default-shadow flight-details-item position-relative">
      <ProposalHeart
        onClickPropsal={onClickHeart}
        active={!!proposal?.flights.some((fl) => fl.item.id === flight.id)}
      />
      <div className="title-underline title-header-grey text-lg mb-20">
        {t('flight.order_details')}
      </div>
      <div className="flight-details-order">
        <div className="flight-details-info text-lh">
          <div className="flex-row mb-20 text-sm">
            <div className="mr-10">
              <div className="mb-5">{t('flight.departure_date')}</div>
              <div className="mb-5">{t('flight.return_date')}</div>
              <div className="mb-5">{t('filter.duration')}</div>
              <div className="mb-5">{t('filter.airline')}</div>
              <div className="mb-5">{t('flight.from')}</div>
              <div className="mb-5">{t('flight.to')}</div>{' '}
              {!isTT && flight.flightSource?.includes('ALP') && (
                <div className="mb-5">{t('package.provider')}</div>
              )}
            </div>
            {!!bounds.length && (
              <div className="text-bold">
                <div className="mb-5">
                  {dateFormat(bounds[0].departureDateTime, 'EEE - dd MMMM, yyyy')}
                </div>
                <div className="mb-5">
                  {dateFormat(bounds[bounds.length - 1].arrivalDateTime, 'EEE - dd MMMM, yyyy')}
                </div>
                <div className="mb-5">{getDurationText()}</div>
                <div className="mb-5">{getAirlinesText()}</div>
                <div className="mb-5 flex-v-center">
                  <ReactCountryFlag
                    className="mr-5"
                    countryCode={bounds[0].departureAirport.countryCode}
                    svg
                  />
                  <span dangerouslySetInnerHTML={{ __html: getFlightFromText() }}></span>
                </div>
                <div className="mb-5 flex-v-center">
                  <ReactCountryFlag
                    className="mr-5"
                    countryCode={bounds[0].arrivalAirport.countryCode}
                    svg
                  />
                  <span dangerouslySetInnerHTML={{ __html: getFlightToText() }}></span>
                </div>
                {!isTT && flight.flightSource?.includes('ALP') && (
                  <div className="mb-5">{flight.provider}</div>
                )}
              </div>
            )}
          </div>

          {includesUsa() && (
            <div className="color-danger text-bold text-sm text-lh-sm mb-20">
              {t('flight.usa_warning')}
            </div>
          )}

          <div className="flight-details-table">
            <div className="table-row text-bold">
              <span className="cell">{t('title')}</span>
              <span className="cell">{t('flight.total_average_passenger')}</span>
              <span className="cell">{t('total')}</span>
            </div>
            {flight?.flightPricesByPDT?.map((passenger, index) => (
              <div className="table-row" key={index}>
                <span className="cell">
                  <span className="text-bold">{getPassengerStr(t, passenger.paxPDT)}: </span>
                  <span>{passenger.quantity}</span>
                </span>
                <div className="cell">
                  <div>
                    <span>
                      {formatPriceFlight(passenger.grossAndTaxWithMarkup, passenger.quantity)}{' '}
                    </span>
                    <Tooltip
                      position="top"
                      children={
                        <FontAwesomeIcon
                          icon={
                            (index === shownDetailsIndex
                              ? faChevronCircleUp
                              : faChevronCircleDown) as any
                          }
                          onClick={() => onClickChevron(index)}
                        />
                      }
                      content={t('flight.show_details')}
                    />
                  </div>

                  {index === shownDetailsIndex && (
                    <div className="mt-5">
                      {t('basis')}{' '}
                      <span className="text-bold">
                        {formatPriceFlight(passenger.grossWithMarkup, passenger.quantity)}{' '}
                      </span>{' '}
                      + {t('taxes')}{' '}
                      <span className="text-bold">
                        {formatPriceFlight(passenger.tax, passenger.quantity)}{' '}
                      </span>{' '}
                      = {t('price')}{' '}
                      <span className="text-bold text-contrast">
                        {formatPriceFlight(passenger.grossAndTaxWithMarkup, passenger.quantity)}
                      </span>
                    </div>
                  )}
                </div>
                <span className="cell">{formatPriceFlight(passenger.grossAndTaxWithMarkup)}</span>
              </div>
            ))}
            <div className="table-bottom">
              <span className="text-bold">{t('flight.total_per_package')}</span>
              <span className="price text-bold">{recountPrice()}</span>
            </div>
          </div>
        </div>
        <div className="flight-details-map">
          <div className="map">
            <img src={props.mapUrl} alt="map" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default withErrorBoundary(OrderDetails, 'OrderDetails');
